import React, { useState } from 'react';
import SEO from '../components/SEO';
import { useLanguage } from '../context/LanguageContext';
import { useTranslations } from '../utils/translationDictionaries';
import './Contact.css';

const Contact = () => {
  const { language } = useLanguage();
  const { t } = useTranslations(language);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    const web3formsAccessKey = process.env.REACT_APP_WEB3FORMS_ACCESS_KEY;
    
    if (!web3formsAccessKey) {
      setError(t('contact.configError'));
      setLoading(false);
      return;
    }
    
    try {
      // Prepare the form data for Web3Forms
      const formPayload = {
        access_key: web3formsAccessKey,
        name: formData.name,
        email: formData.email,
        phone: formData.phone || t('contact.notProvided'),
        message: formData.message,
        subject: `ARG Pagina Web - ${formData.name}`,
        from_name: 'Alicea Realty Group Website',
      };
      
      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(formPayload)
      });
      
      const result = await response.json();
      
      if (result.success) {
        setSubmitted(true);
        setTimeout(() => {
          setSubmitted(false);
          setFormData({ name: '', email: '', phone: '', message: '' });
        }, 3000);
      } else {
        setError(t('contact.errorMessage'));
      }
    } catch (err) {
      console.error('Form submission error:', err);
      setError(t('contact.connectionError'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="contact">
      <SEO 
        title="Contáctenos | Alicea Realty Group LLC | Noemi Alicea | Bienes Raíces Puerto Rico"
        description="Contáctenos para comprar, vender o alquilar propiedades en Puerto Rico. Noemí Alicea González, Corredora Autorizada. Teléfono: (787) 342-1648. Email: alicearealtygroupllc@gmail.com"
        keywords="contacto bienes raíces Puerto Rico, contactar realtor Puerto Rico, Alicea Realty contacto, consulta inmobiliaria Puerto Rico, Noemi Alicea contacto, Noemí Alicea González teléfono"
        ogTitle="Contáctenos | Alicea Realty Group LLC"
        ogDescription="Contáctenos para comprar, vender o alquilar propiedades en Puerto Rico. Noemí Alicea González, Corredora Autorizada."
        canonical="https://www.alicearealtypr.com/contact"
      />
      
      <div className="contact-container">
        <div className="contact-header">
          <h1>{t('contact.title')}</h1>
          <p className="contact-intro">
            {language === 'es' 
              ? '¿Tiene preguntas? Nos encantaría saber de usted. Envíenos un mensaje y le responderemos lo antes posible.'
              : 'Have questions? We\'d love to hear from you. Send us a message and we\'ll respond as soon as possible.'}
          </p>
        </div>

        <div className="contact-content">
          <div className="contact-info">
            <h2>{language === 'es' ? 'Información de Contacto' : 'Contact Information'}</h2>
            <div className="info-cards">
              <div className="info-card">
                <div className="info-card-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M1.5 8.67v8.58a3 3 0 003 3h15a3 3 0 003-3V8.67l-8.928 5.493a3 3 0 01-3.144 0L1.5 8.67z" />
                    <path d="M22.5 6.908V6.75a3 3 0 00-3-3h-15a3 3 0 00-3 3v.158l9.714 5.978a1.5 1.5 0 001.572 0L22.5 6.908z" />
                  </svg>
                </div>
                <div className="info-card-content">
                  <h3>{t('contact.email')}</h3>
                  <a href="mailto:alicearealtygroupllc@gmail.com">alicearealtygroupllc@gmail.com</a>
                </div>
              </div>

              <div className="info-card">
                <div className="info-card-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                    <path fillRule="evenodd" d="M1.5 4.5a3 3 0 013-3h1.372c.86 0 1.61.586 1.819 1.42l1.105 4.423a1.875 1.875 0 01-.694 1.955l-1.293.97c-.135.101-.164.249-.126.352a11.285 11.285 0 006.697 6.697c.103.038.25.009.352-.126l.97-1.293a1.875 1.875 0 011.955-.694l4.423 1.105c.834.209 1.42.959 1.42 1.82V19.5a3 3 0 01-3 3h-2.25C8.552 22.5 1.5 15.448 1.5 6.75V4.5z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="info-card-content">
                  <h3>{t('contact.phone')}</h3>
                  <a href="tel:+17873421648">(787) 342-1648</a>
                </div>
              </div>
            </div>
          </div>

          <form className="contact-form" onSubmit={handleSubmit}>
            <h2>{language === 'es' ? 'Envíanos un Mensaje' : 'Send Us a Message'}</h2>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="name">{t('contact.name')} *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder={language === 'es' ? 'Su nombre completo' : 'Your full name'}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="email">{t('contact.email')} *</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder={language === 'es' ? 'su@email.com' : 'your@email.com'}
                  required
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="phone">{t('contact.phone')}</label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="(787) 123-4567"
              />
            </div>

            <div className="form-group">
              <label htmlFor="message">{t('contact.message')} *</label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                rows="6"
                placeholder={language === 'es' ? 'Cuéntenos cómo podemos ayudarle...' : 'Tell us how we can help you...'}
                required
              ></textarea>
            </div>

            <button type="submit" className="submit-button" disabled={loading}>
              <span>{loading ? t('contact.sending') : t('contact.send')}</span>
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                <path d="M3.478 2.405a.75.75 0 00-.926.94l2.432 7.905H13.5a.75.75 0 010 1.5H4.984l-2.432 7.905a.75.75 0 00.926.94 60.519 60.519 0 0018.445-8.986.75.75 0 000-1.218A60.517 60.517 0 003.478 2.405z" />
              </svg>
            </button>

            {error && (
              <div className="error-message">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                  <path fillRule="evenodd" d="M9.401 3.003c1.155-2 4.043-2 5.197 0l7.355 12.748c1.154 2-.29 4.5-2.599 4.5H4.645c-2.309 0-3.752-2.5-2.598-4.5L9.4 3.003zM12 8.25a.75.75 0 01.75.75v3.75a.75.75 0 01-1.5 0V9a.75.75 0 01.75-.75zm0 8.25a.75.75 0 100-1.5.75.75 0 000 1.5z" clipRule="evenodd" />
                </svg>
                {error}
              </div>
            )}

            {submitted && (
              <div className="success-message">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                  <path fillRule="evenodd" d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12zm13.36-1.814a.75.75 0 10-1.22-.872l-3.236 4.53L9.53 12.22a.75.75 0 00-1.06 1.06l2.25 2.25a.75.75 0 001.14-.094l3.75-5.25z" clipRule="evenodd" />
                </svg>
                {t('contact.successMessage')}
              </div>
            )}
          </form>
        </div>
      </div>
    </div>
  );
};

export default Contact;
